if (!customElements.get('zd-display-accessories')) {
  customElements.define('zd-display-accessories', class extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.productForm);
      this.quantity = document.querySelector(`input[name="quantity"][form="${this.dataset.productForm}"]`);
      this.variants = JSON.parse(this.querySelector('[data-zd-support-variants]').textContent);
      this.fieldset = this.querySelector('fieldset');
      this.summary = this.querySelector('[data-zd-support-summary]');
      this.rail = this.querySelector('[data-zd-support-rail]');
      this.controls = this.querySelector('[data-zd-support-controls]');
      this.defaultSummary = this.summary.textContent;
      this.formatMoney = new Intl.NumberFormat(this.dataset.locale || 'it', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.onChange = () => this.updateSummary();
      this.onClick = event => {
        const scrollButton = event.target.closest('[data-zd-support-scroll]');
        if (scrollButton) {
          const card = this.rail.querySelector('[data-zd-support-card]:not([hidden])');
          if (!card) return;
          const style = getComputedStyle(this.rail);
          const step = card.getBoundingClientRect().width + parseFloat(style.columnGap);
          const direction = Number(scrollButton.dataset.zdSupportScroll) * (style.direction === 'rtl' ? -1 : 1);
          this.rail.scrollBy({ left: step * direction, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          return;
        }
      };
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.updateControls();
        });
      };
      this.rail.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.rail);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.quantity?.addEventListener('change', this.onChange);
      this.quantity?.addEventListener('input', this.onChange);
      this.unsubscribe = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.variantChange, event => {
        if (event.data.sectionId === this.dataset.sectionId) this.updateVariant(event.data.variant);
      }) : null;
      this.unsubscribeQuantity = typeof subscribe === 'function' ? subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.updateSummary()) : null;
      this.fieldset.disabled = false;
      this.updateVariant(this.variants.find(variant => String(variant.id) === this.form?.querySelector('[name="id"]')?.value));
    }
    disconnectedCallback() {
      this.rail.removeEventListener('scroll', this.onScroll);
      this.resizeObserver.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      this.quantity?.removeEventListener('change', this.onChange);
      this.quantity?.removeEventListener('input', this.onChange);
      this.unsubscribe?.();
      this.unsubscribeQuantity?.();
    }
    get selection() {
      const input = this.querySelector('[data-zd-support]:checked');
      return input && !input.disabled ? input : null;
    }
    updateVariant(variant) {
      const model = variant?.sku?.toUpperCase();
      let reset = false;
      this.querySelectorAll('[data-zd-support-card]').forEach(card => {
        const input = card.querySelector('[data-zd-support]');
        const visible = !!model && card.dataset.models.split(',').includes(model);
        const allowed = visible && variant?.available && card.dataset.available === 'true';
        if (input.checked && !allowed) reset = true;
        card.hidden = !visible;
        input.disabled = !allowed;
      });
      this.hidden = !this.querySelector('[data-zd-support-card]:not([hidden])');
      if (reset || !this.selection) this.querySelector('input[value=""]').checked = true;
      this.updateSummary(reset);
      this.onScroll();
    }
    updateControls() {
      const cards = [...this.rail.querySelectorAll('[data-zd-support-card]:not([hidden])')];
      this.controls.hidden = this.rail.scrollWidth <= this.rail.clientWidth + 1;
      const offset = Math.abs(this.rail.scrollLeft);
      this.querySelector('[data-zd-support-scroll="-1"]').disabled = offset <= 1;
      this.querySelector('[data-zd-support-scroll="1"]').disabled = offset + this.rail.clientWidth >= this.rail.scrollWidth - 1;
      const bounds = this.rail.getBoundingClientRect();
      const visible = cards.map((card, index) => {
        const rect = card.getBoundingClientRect();
        return Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left) > rect.width / 2 ? index : -1;
      }).filter(index => index >= 0);
      if (visible.length) this.querySelector('[data-zd-support-range]').textContent = this.dataset.rangeLabel
        .replace('__first__', visible[0] + 1).replace('__last__', visible[visible.length - 1] + 1).replace('__total__', cards.length);
    }
    updateSummary(reset = false) {
      const selected = this.selection;
      this.querySelectorAll('[data-zd-support-card]').forEach(card => card.classList.toggle('is-selected', card.querySelector('[data-zd-support]').checked));
      if (selected) {
        const quantity = Math.max(1, Number(this.quantity?.value) || 1);
        const amount = this.formatMoney.format(Number(selected.dataset.price) * quantity / 122);
        this.summary.textContent = this.dataset.summaryLabel.replace('__quantity__', quantity).replace('__title__', selected.dataset.title).replace('__price__', amount) + ' ' + this.dataset.priceSuffix;
      } else {
        this.summary.textContent = reset ? this.dataset.resetLabel : this.defaultSummary;
      }
      this.classList.toggle('has-selection', !!selected);
      const acceleratedCheckout = this.form?.querySelector('.shopify-payment-button');
      if (acceleratedCheckout) acceleratedCheckout.hidden = !!selected;
    }
    appendCartItems(formData) {
      const selected = this.selection;
      if (!selected) return false;
      const entries = [...formData.entries()];
      for (const [name, value] of entries) {
        if (name === 'id' || name === 'quantity' || name === 'selling_plan') {
          formData.delete(name);
          formData.append(`items[0][${name}]`, value);
        } else if (/^properties\[.+\]$/.test(name)) {
          formData.delete(name);
          formData.append(`items[0][properties]${name.slice(10)}`, value);
        }
      }
      const quantity = String(Math.max(1, Number(formData.get('items[0][quantity]')) || 1));
      formData.set('items[0][quantity]', quantity);
      formData.append('items[1][id]', selected.value);
      formData.append('items[1][quantity]', quantity);
      return true;
    }
    setBusy(busy) {
      this.fieldset.disabled = busy;
      if (!busy) this.updateSummary();
    }
  });
}
